import { OrganizationsState } from '../organizations/OrganizationsState';
import { CareMapState } from '../caremap/CareMapState';
import { CareMapOnScreen } from '../caremap/CareMapOnScreen';
import { IOrganization } from '../organizations/IOrganization';
import { ICareMap, ICareMapKey, INewCareMap } from '../caremap/ICareMap';

// The active care map is a saved one once it has an owner and an id; one still being created has neither.
const isSavedCareMap = (careMap: INewCareMap | ICareMap | undefined): careMap is ICareMap => {
    return Boolean(careMap && 'userId' in careMap && 'careMapId' in careMap && careMap.careMapId);
};

type IBeaKind = 'family' | 'agency';

/**
 * Which Bea an entry point opens. A member of an organization's staff gets agency Bea when no care
 * map is on screen, or when the one on screen is a client the organization holds; on their own
 * family's care map they get family Bea, as anyone does. The organization's chat links are the api's
 * statement that this member may ask across its caseload.
 */
export class BeaSelector {
    static inject = () => [OrganizationsState, CareMapState, CareMapOnScreen];
    constructor(private orgState: OrganizationsState, private careMapState: CareMapState, private onScreen: CareMapOnScreen) {}

    getAgencyOrganization = (): IOrganization | undefined => {
        const organization = this.orgState.getMostRecentOrganization();
        if (!organization?.links.chatStream || !organization.links.chats) {
            return undefined;
        }
        return organization;
    };

    select = (): IBeaKind => {
        const organization = this.getAgencyOrganization();
        if (!organization) {
            return 'family';
        }
        const activeCareMap = this.getCareMapOnScreen();
        if (!activeCareMap) {
            return 'agency';
        }
        return this.isClientOf(organization, activeCareMap) ? 'agency' : 'family';
    };

    /** The saved care map a page is showing, if any. */
    getCareMapOnScreen = (): ICareMap | undefined => {
        const activeCareMap = this.careMapState.getActiveCaremap();
        if (!isSavedCareMap(activeCareMap) || activeCareMap.careMapId !== this.onScreen.getCareMapId()) {
            return undefined;
        }
        return activeCareMap;
    };

    isClientOf = (organization: IOrganization, careMapKey: ICareMapKey): boolean => {
        return this.orgState.getOrganizationCareMaps(organization.orgId)
            .some(candidate => candidate.careMapId === careMapKey.careMapId && candidate.userId === careMapKey.userId);
    };
}
